import './Switch.scss'
import { useId, useState, type ChangeEvent, type ComponentProps, type ReactNode } from 'react'
import { clx } from '../clx'

export interface SwitchProps extends Omit<
  ComponentProps<'input'>,
  'type' | 'role' | 'value' | 'defaultValue' | 'onChange' | 'size'
> {
  /** Visible label. Omit only when the switch is labelled elsewhere (Field, aria-label). */
  label?: ReactNode
  /** Helper text under the label, linked through aria-describedby. */
  description?: ReactNode
  /** Typed value first; not called while read-only. */
  onChange?: (checked: boolean, event: ChangeEvent<HTMLInputElement>) => void
}

/** Boolean on/off toggle (the portal's BooleanInput): a native checkbox with role="switch". */
export function Switch({
  label,
  description,
  checked,
  defaultChecked,
  onChange,
  readOnly = false,
  className,
  ref,
  id,
  'aria-describedby': describedBy,
  ...rest
}: SwitchProps) {
  const generated = useId()
  const inputId = id ?? `switch-${generated}`
  const descriptionId = description !== undefined ? `${inputId}-description` : undefined
  const [uncontrolled, setUncontrolled] = useState(defaultChecked ?? false)
  const isOn = checked ?? uncontrolled
  const describedByIds = [describedBy, descriptionId].filter(Boolean).join(' ') || undefined

  return (
    <div
      className={clx(
        'switch',
        rest.disabled && 'switch--is-disabled',
        readOnly && 'switch--is-read-only',
        className
      )}
    >
      <label className="switch__main" htmlFor={inputId}>
        <input
          {...rest}
          ref={ref}
          id={inputId}
          type="checkbox"
          role="switch"
          className="switch__input"
          checked={isOn}
          aria-checked={isOn}
          aria-readonly={readOnly || undefined}
          aria-describedby={describedByIds}
          onChange={(event) => {
            if (readOnly) return
            if (checked === undefined) setUncontrolled(event.target.checked)
            onChange?.(event.target.checked, event)
          }}
        />
        {label !== undefined && <span className="switch__label">{label}</span>}
      </label>
      {description !== undefined && (
        <p id={descriptionId} className="switch__description">
          {description}
        </p>
      )}
    </div>
  )
}
